import { NgIf, NgFor, AsyncPipe } from '@angular/common'
import { ChangeDetectorRef, Component, Optional, ViewChild } from '@angular/core'
import { RouterLink } from '@angular/router'
import {
  CmsSearchBoxComponent,
  RoutingService,
  WindowRef,
  UrlPipe,
  TranslatePipe
} from '@spartacus/core'
import {
  CmsComponentData,
  SearchBoxComponent,
  IconComponent,
  MediaComponent
} from '@spartacus/storefront'
import { BehaviorSubject, Observable, Subscription, filter, switchMap, tap } from 'rxjs'
import { EbSpinnerComponent } from '../../../../components/spinner/eb-spinner.component'
import { EbProduct } from '../../../product/interfaces/eb-product.interface'
import { EbSearchResults } from './interfaces/eb-search-result.interface'
import { EbSearchBoxComponentService } from './services/eb-search-box-component.service'

@Component({
  selector: 'eb-search-box',
  templateUrl: './eb-search-box.component.html',
  styleUrls: ['./eb-search-box.component.scss'],
  imports: [
    NgIf,
    EbSpinnerComponent,
    IconComponent,
    NgFor,
    RouterLink,
    MediaComponent,
    AsyncPipe,
    UrlPipe,
    TranslatePipe
  ]
})
export class EbSearchBoxComponent extends SearchBoxComponent {
  @ViewChild('searchInput') searchInput
  isVisible = false
  searchTermLength: number
  isSearching$ = new BehaviorSubject(false)
  avoidClose = false
  results$: Observable<EbSearchResults> = this.config$.pipe(
    switchMap(config => this.searchBoxComponentService.getResults(config)),
    tap(() => this.setIsSearching(false))
  )
  spinnerDelay
  subscription: Subscription

  constructor(
    protected searchBoxComponentService: EbSearchBoxComponentService,
    @Optional()
    protected componentData: CmsComponentData<CmsSearchBoxComponent>,
    protected winRef: WindowRef,
    protected routingService: RoutingService,
    protected cd: ChangeDetectorRef
  ) {
    super(searchBoxComponentService, componentData, winRef, routingService)
  }

  open(): void {
    super.open()
    this.isVisible = true
    this.cd.detectChanges()
    this.searchInput.nativeElement.focus()

    this.avoidClose = false
  }

  get isSearching(): Observable<boolean> {
    return this.isSearching$
  }

  close(force = false): void {
    if (!this.avoidClose) {
      this.blurSearchBox()
      this.isVisible = false
      this.searchBoxComponentService.clearResults()
    }
  }

  clear(): void {
    this.avoidClose = true
    this.clearInputField()
    this.searchBoxComponentService.clearResults()
    this.searchTermLength = 0
    this.chosenWord = ''
  }

  getProductUrl(result: EbSearchResults, product?: EbProduct) {
    if (result.keywordRedirectUrl) {
      const [urlWithoutParams] = result.keywordRedirectUrl.split(/\?/)
      return urlWithoutParams
    } else {
      return {
        cxRoute: 'ebParentProduct',
        params: product
      }
    }
  }

  getQueryParams(url: string): { [key: string]: string } {
    const [, paramString] = url.split(/\?/)
    if (paramString) {
      const params = new URLSearchParams(paramString)
      const searchTerm = params.get('searchTerm')
      return { searchTerm }
    }
    return {}
  }

  search(query: string): void {
    this.setIsSearching(true)
    this.searchTermLength = query.length
    super.search(query)
  }

  launchSearchResult(query: string): void {
    if (!query || query.trim().length === 0) {
      return
    }
    this.close()
    this.searchBoxComponentService.launchSearchPage(query)
    if (this.searchBoxComponentService.ebHasKeywordRedirect$.value === true) {
      this.routingService.goByUrl(this.searchBoxComponentService.keywordRedirectUrl)
    }
    this.search(query)
  }

  setIsSearching(isSearching) {
    // this delays the appearance of the loading spinner in the search-box a bit so the spinner doesn´t flicker with every keystroke
    if (isSearching) {
      if (this.spinnerDelay) {
        clearTimeout(this.spinnerDelay)
        this.spinnerDelay = null
      }
      this.spinnerDelay = setTimeout(() => {
        this.isSearching$.next(isSearching)
      }, 100)
    } else {
      this.isSearching$.next(isSearching)
      clearTimeout(this.spinnerDelay)
      this.spinnerDelay = null
    }
  }

  clearInputField() {
    const inputElement = this.searchInput?.nativeElement
    if (inputElement) {
      inputElement.value = ''
      inputElement.focus()
    }
  }

  ngOnInit(): void {
    this.subscription = this.routingService
      .getRouterState()
      .pipe(filter(data => !data.nextState))
      .subscribe(() => {
        this.clear()
      })
  }
}
